import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '../components/Icon';
import { faBolt, faScrewdriverWrench, faShieldHalved, faTruckFast, faArrowRight, faHouse, faBuilding, faChargingStation, faGaugeHigh } from '@fortawesome/free-solid-svg-icons';
import { useSettings } from '../lib/store';
import { useFetch } from '../lib/useFetch';
import { useI18n } from '../lib/i18n';
import { ProductGrid } from '../components/ProductCard';
import { SectionHead } from '../components/ui';
import { responsive } from '../lib/img';

const CATEGORY_TILES = [
  { to: '/shop?category=chargers', img: '/uploads/dc-60kw.webp', title: 'EV Chargers', sub: 'AC & DC · Home & Public' },
  { to: '/installation', img: '/uploads/svc-residential.jpg', title: 'Installation Services', sub: 'Home, commercial & DC civil works' },
  { to: '/shop?category=adapters', img: '/uploads/adapter.svg', title: 'EV Adapters', sub: 'Type 1 · Type 2 · GB/T · CCS2' },
  { to: '/shop?category=cables', img: '/uploads/cable.svg', title: 'EV Cables', sub: 'Mode 3 & portable chargers' },
];

const QUICK = [
  { to: '/shop?category=chargers&type=AC&usage=private', icon: faHouse, title: 'Home AC Chargers', sub: '7 – 22 kW wallboxes' },
  { to: '/shop?category=chargers&type=AC&usage=public', icon: faBuilding, title: 'Commercial AC', sub: 'Malls, offices, buildings' },
  { to: '/shop?category=chargers&type=DC&usage=private', icon: faChargingStation, title: 'Private DC', sub: 'Fleets & showrooms' },
  { to: '/shop?category=chargers&type=DC&usage=public', icon: faGaugeHigh, title: 'Public DC Fast', sub: '30 – 300 kW stations' },
];

export default function Home() {
  const { settings: s, money } = useSettings();
  const { t, pick } = useI18n();
  const featured = useFetch('/api/products?featured=1');
  const dc = useFetch('/api/products?category=chargers&type=DC&sort=price-asc');

  const why = [
    [faBolt, t('AC & DC range'), t('From 7kW home wallboxes up to 300kW public hyper chargers.')],
    [faScrewdriverWrench, t('Certified installation'), t('Licensed engineers, DEWA / ADDC / SEWA approval support.')],
    [faShieldHalved, t('Warranty & AMC'), t('Up to 3-year warranty and annual maintenance contracts.')],
    [faTruckFast, t('Fast UAE delivery'), t('Free delivery on orders over {amount}.', { amount: money(s.freeDeliveryOver) })],
  ];

  return (
    <>
      <section className="overflow-hidden bg-[radial-gradient(1200px_500px_at_85%_20%,#1f9be7_0%,transparent_60%),linear-gradient(135deg,#0b2540_0%,#0e3a64_60%,#0d5f9c_100%)] text-white">
        <div className="shell grid items-center gap-10 py-14 lg:grid-cols-[1.1fr_1fr] lg:py-20">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3 py-1.5 text-[13px] font-semibold text-teal-200">
              <FontAwesomeIcon icon={faBolt} /> {t('Certified EV charging partner in the UAE')}
            </span>
            <h1 className="mt-5 mb-4 text-[clamp(2.1rem,5vw,3.5rem)] leading-[1.08] font-extrabold">{pick(s, 'heroTitle')}</h1>
            <p className="mb-7 max-w-xl text-lg text-brand-100/80">{pick(s, 'heroSubtitle')}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/shop?category=chargers" className="btn btn-primary">{t('Shop EV Chargers')} <FontAwesomeIcon icon={faArrowRight} /></Link>
              <Link to="/installation" className="btn btn-ghost-light">{t('Book Installation')}</Link>
            </div>
            <dl className="mt-9 flex flex-wrap gap-8">
              {[['7–300 kW', 'AC & DC range'], ['24/7', 'Support & monitoring'], ['UAE-wide', 'Delivery & install']].map(([v, l]) => (
                <div key={l}><dt className="text-2xl font-extrabold">{t(v)}</dt><dd className="text-[13px] text-brand-200/80">{t(l)}</dd></div>
              ))}
            </dl>
          </div>
          <div className="order-first max-w-md lg:order-none lg:max-w-none">
            <img {...responsive('/uploads/ac-7-22kw.png', '(min-width: 1024px) 45vw, min(448px, 92vw)', 960)} width="800" height="800" fetchPriority="high"
              alt={t('Tatheer home wallbox charger')} className="aspect-square w-full rounded-3xl object-cover shadow-lift" />
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="shell">
          <SectionHead title={t('Shop by category')} subtitle={t('Everything you need to charge at home, at work or on the road.')} />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CATEGORY_TILES.map((c) => (
              <Link key={c.title} to={c.to} className="group relative isolate flex min-h-52 flex-col justify-end overflow-hidden rounded-2xl bg-navy p-5 text-white shadow-card transition hover:-translate-y-1 sm:min-h-60">
                <img {...responsive(c.img, '(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw', 640)} alt="" loading="lazy" decoding="async" className="absolute inset-0 -z-20 size-full object-cover opacity-60 transition duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent from-20% to-navy/95" />
                <h3 className="text-xl font-bold">{t(c.title)}</h3>
                <p className="text-sm text-brand-100/80">{t(c.sub)}</p>
              </Link>
            ))}
          </div>
          <div className="mt-4 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
            {QUICK.map((q) => (
              <Link key={q.title} to={q.to} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 font-bold transition hover:border-brand-500 hover:text-brand-600">
                <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-soft text-brand-500"><FontAwesomeIcon icon={q.icon} /></span>
                <span>{t(q.title)}<small className="block text-xs font-medium text-slate-500">{t(q.sub)}</small></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-soft py-16">
        <div className="shell">
          <SectionHead title={t('Featured products')} subtitle={t('Best-sellers and current offers.')}
            action={<Link to="/shop" className="font-bold text-brand-600 hover:underline">{t('View all products')} <FontAwesomeIcon icon={faArrowRight} /></Link>} />
          <ProductGrid products={(featured.data || []).slice(0, 8)} loading={featured.loading} />
        </div>
      </section>

      <section className="py-16">
        <div className="shell">
          <SectionHead title={t('DC fast chargers')} subtitle={t('High-power charging for fleets, fuel stations and public hubs.')}
            action={<Link to="/shop?category=chargers&type=DC" className="font-bold text-brand-600 hover:underline">{t('All DC chargers')} <FontAwesomeIcon icon={faArrowRight} /></Link>} />
          <ProductGrid products={(dc.data || []).slice(0, 4)} loading={dc.loading} />
        </div>
      </section>

      <section className="bg-soft py-16">
        <div className="shell">
          <SectionHead title={t('Why choose Tatheer')} />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {why.map(([icon, h, p]) => (
              <div key={h} className="card p-6">
                <FontAwesomeIcon icon={icon} className="text-2xl text-brand-500" />
                <h3 className="mt-3 mb-1.5 font-bold">{h}</h3>
                <p className="text-sm text-slate-500">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="shell">
          <div className="flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-gradient-to-br from-navy to-brand-700 p-8 text-white sm:p-10">
            <div>
              <h2 className="mb-1.5 text-2xl font-extrabold">{t('Planning a charging site?')}</h2>
              <p className="text-brand-100/80">{t('Get a free site survey and quotation for home, commercial or fleet charging.')}</p>
            </div>
            <Link to="/installation#quote" className="btn btn-primary">{t('Request a free survey')}</Link>
          </div>
        </div>
      </section>
    </>
  );
}
